/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useRef, useState } from "react";
import "./CSS/CalendarScreen.css";
import locationIcon from "../../assets/images/calendar-location-icon.svg";
import eventVideoIcon from "../../assets/images/event-video-icon.svg";
import exclusiveForYouIcon from "../../assets/images/calendar-exclusive-for-you-button.svg";
import exclusiveIcon from "../../assets/images/calendar-exclusive-button.svg.svg";
import sepratorFirstImage from "../../assets/images/calendar-seprator-calm.png";
import sepratorSecondImage from "../../assets/images/calendar-seprator-thoughts.png";
import CalendarStrip from "./Views/CalendarStrip";
import { useNavigate } from "react-router-dom";
import ROUTE_PATHS from "utils/routePaths";
import ReserveAcknowledgementDialog from "components/elements/ReserveAcknowledgementDialog";
import CalendarScreenHeader from "./Views/CalendarScreenHeader";
import BecomeMemberDialog from "components/elements/BecomeMemberDialog";
import placeholderGif from "../../assets/gif/app-loader.gif";
import reserveSlotInfo from "../../assets/images/reserve-slot-info-icon.svg";
import { useMutation, useQuery } from "@tanstack/react-query";
import useWSMApi from "services/api/useWSMApi";
import dayjs from "dayjs";
import AppLoader from "components/elements/AppLoader";
import {
  API_DATE_FORMAT,
  CALENDAR_RESERVATION_TABS,
  CALENDAR_RESERVATION_TYPE,
  LaunchDateMBStudio,
  LaunchDateMeetingRooms,
  LaunchDateRetreat,
  LaunchDateWorkspace,
  MEMBERSHIP_TYPE,
  MENU_ITEM,
  PROFILE_STATUS,
  PURCHASE_TYPE,
} from "utils/constants";
import {
  checkNonMemberCredits,
  getDFWIcons,
  getLaunchDateStatus,
  getWorkspaceSlotDuration,
  getWorkspaceSlotTime,
} from "utils/utils";
import SnackbarAlert from "components/elements/SnackbarAlert";
import { useStorage } from "services/StorageProvider";
import InformationPopup from "components/elements/InformationPopup";
import { STORAGE_KEY, STRING } from "utils/strings";
import { useTabBar } from "services/TabBarProvider";
import {
  trackEvent,
  trackItemClick,
  trackListingView,
  trackReserveClick,
  trackSlotSelected,
} from "utils/analytics";

const CalendarScreen: FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const storedDate = sessionStorage.getItem(
      STORAGE_KEY.CALENDAR_SELECTED_DATE,
    );
    return storedDate ? new Date(storedDate) : new Date();
  });
  const [selectedTab, setSelectedTab] = useState<string>(() => {
    const storedTab = sessionStorage.getItem(STORAGE_KEY.CALENDAR_SELECTED_TAB);
    return storedTab ?? CALENDAR_RESERVATION_TYPE.DF_WORKSPACE;
  });
  const [showAcknowledgementDialog, setShowAcknowledgementDialog] =
    useState<boolean>(false);
  const [showBecomeMemberDialog, setShowBecomeMemberDialog] =
    useState<boolean>(false);
  const [showInformationPopup, setShowInformationPopup] =
    useState<boolean>(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const navigate = useNavigate();
  const { setSelectedTabBar } = useTabBar();
  const { userProfileData, membershipListData } = useStorage();
  const { fetchWSMSlots, reserveWSMSlot, fetchReservations } = useWSMApi();
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isActiveMember, setIsActiveMember] = useState<boolean>(false);
  const hideCalendarStrip = selectedTab === CALENDAR_RESERVATION_TYPE.PASSES;
  const creativeLastRef = useRef<string>("");
  const innovateLastRef = useRef<string>("");
  const communicateLastRef = useRef<string>("");
  const imageIndexRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setShowAnimation(true), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setIsActiveMember(
      userProfileData?.credit?.status === PROFILE_STATUS.MEMBER,
    );
  }, []);

  useEffect(() => {
    const tabIndex = CALENDAR_RESERVATION_TABS.findIndex(
      (tab) => tab.value === selectedTab,
    );
    tabRefs.current[tabIndex]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedTab]);

  useEffect(() => {
    sessionStorage.setItem(
      STORAGE_KEY.CALENDAR_SELECTED_DATE,
      selectedDate.toISOString(),
    );
    sessionStorage.setItem(STORAGE_KEY.CALENDAR_SELECTED_TAB, selectedTab);
    trackListingView(selectedTab);
  }, [selectedDate, selectedTab]);

  useEffect(() => {
    setSelectedDate(new Date());
  }, [hideCalendarStrip]);

  const { data: slotsData, isLoading } = useQuery({
    queryKey: ["get-wsm-slots", selectedDate, selectedTab],
    queryFn: () =>
      fetchWSMSlots({
        date: selectedDate ? dayjs(selectedDate).format(API_DATE_FORMAT) : "",
        type: selectedTab,
      }),
    retry: false,
  });

  const { data: reservationsData, isError: reservationError } = useQuery({
    queryKey: ["fetch-user-reservations"],
    queryFn: () => fetchReservations(),
    retry: false,
  });

  const slotReservationMutation = useMutation({
    mutationFn: () => reserveWSMSlot({ slot_id: selectedSlot?.id }),
    onSuccess: () => {
      setShowAcknowledgementDialog(false);
      trackEvent("reservation_created", {
        slot_id: String(selectedSlot?.id ?? ""),
        service_type: selectedSlot?.service_type ?? "",
      });
      const paymentData = {
        title: `${selectedSlot?.title}`,
        type: PURCHASE_TYPE.EVENT,
      };
      localStorage.setItem(
        STORAGE_KEY.PAYMENT_SUCCESS_DATA,
        JSON.stringify(paymentData),
      );
      navigate(ROUTE_PATHS.WSM_ALL_SET);
    },
  });

  const handleDateChange = (dateString: string): void => {
    setSelectedDate(new Date(dateString));
  };

  const handleReserveClick = (slot: any) => {
    setSelectedSlot(slot);
    localStorage.setItem(STORAGE_KEY.SELECTED_SLOT_DATA, JSON.stringify(slot));

    trackSlotSelected(slot?.start_time ?? "", slot?.slot_date ?? "");
    trackReserveClick(String(slot?.id ?? ""));

    // Check membership validity for Reservation
    const membershipEndDate = new Date(
      userProfileData?.credit?.membership_end_date,
    );
    const slotEndDate = new Date(slot.end_time);

    if (slotEndDate > membershipEndDate && isActiveMember) {
      setShowInformationPopup(true);
      return;
    }

    // Handle Slot Reservation
    if (slot?.is_exclusive) {
      navigate(ROUTE_PATHS.WSM_CALENDAR_PAYMENT);
    } else if (isActiveMember) {
      setShowAcknowledgementDialog(true);
    } else {
      if (checkNonMemberCredits(userProfileData?.credit, slot)) {
        setShowAcknowledgementDialog(true);
      } else {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PAYMENT);
      }
    }
  };

  const handleRetreatReserveClick = (slot: any) => {
    setSelectedSlot(slot);
    localStorage.setItem(STORAGE_KEY.SELECTED_SLOT_DATA, JSON.stringify(slot));
    if (isActiveMember) {
      const membershipEndDate = new Date(
        userProfileData?.credit?.membership_end_date,
      );
      const slotEndDate = new Date(slot.end_time);

      if (slotEndDate > membershipEndDate) {
        setShowInformationPopup(true);
        return;
      }
      setShowAcknowledgementDialog(true);
    } else {
      navigate(ROUTE_PATHS.WSM_CALENDAR_PAYMENT);
    }
  };

  const handleReservationConfirmClick = () => {
    slotReservationMutation.mutate();
  };

  const handleBookingCardClick = (slot: any, reservation: any) => {
    localStorage.setItem(
      STORAGE_KEY.SELECTED_RESERVATION_DATA,
      JSON.stringify(reservation),
    );
    localStorage.setItem(STORAGE_KEY.SELECTED_SLOT_DATA, JSON.stringify(slot));
    trackItemClick(String(slot?.id ?? ""), slot?.title ?? "");
    if (slot.service_type === CALENDAR_RESERVATION_TYPE.RETREAT) {
      navigate(ROUTE_PATHS.WSM_RETREAT_DETAIL.replace(":slotId", slot?.id));
    } else {
      navigate(ROUTE_PATHS.WSM_WORKSPACE_DETAIL.replace(":slotId", slot?.id));
    }
  };

  const getLoadingTitles = () => {
    switch (selectedTab) {
      case CALENDAR_RESERVATION_TYPE.DF_WORKSPACE:
        return "Warming up the Deep Focus Workstations 🔥";

      case CALENDAR_RESERVATION_TYPE.MB_STUDIO:
        return "Curating your wellness & work energy 💫";

      case CALENDAR_RESERVATION_TYPE.EVENTS:
        return "Charging creativity circuits ⚡";

      case CALENDAR_RESERVATION_TYPE.MEETING_ROOMS:
        return "";

      default:
        return "";
    }
  };

  const isLaunchDatePassed = () => {
    const sDate = new Date(selectedDate);
    sDate.setHours(0, 0, 0, 0);
    return getLaunchDateStatus(sDate, selectedTab);
  };

  const getPlaceholderTitle = () => {
    const sDate = new Date(selectedDate);
    sDate.setHours(0, 0, 0, 0);
    const isSunday = sDate.getDay() === 0;
    if (isSunday) {
      return "Sorry, even The Foundry has Off Days!";
    }
    switch (selectedTab) {
      case CALENDAR_RESERVATION_TYPE.DF_WORKSPACE:
        return sDate < LaunchDateWorkspace
          ? "WorkSpace is coming soon 🌿 Reservations starts from 18th March."
          : "A free day - make it count to Scale Smarter Live better.";

      case CALENDAR_RESERVATION_TYPE.MB_STUDIO:
        return sDate < LaunchDateMBStudio
          ? "MB Studio is coming soon 🌿 Reservations starts from 15th April."
          : "A free day - make it count to Scale Smarter Live better.";

      case CALENDAR_RESERVATION_TYPE.EVENTS:
        return "A free day - make it count to Scale Smarter Live better.";

      case CALENDAR_RESERVATION_TYPE.RETREAT:
        return sDate < LaunchDateRetreat
          ? "Our First Retreat is coming 🌿 Exact Details will be announced on 15th April."
          : "A free day - make it count to Scale Smarter Live better.";

      case CALENDAR_RESERVATION_TYPE.MEETING_ROOMS:
        return sDate < LaunchDateMeetingRooms
          ? "Meeting Rooms is coming soon 🌿 Reservations starts from 15th April."
          : "A free day - make it count to Scale Smarter Live better.";

      default:
        return "A free day - make it count to Scale Smarter Live better.";
    }
  };

  const getRetreatReserveButton = (slot: any) => {
    return (
      <div className="calendar-exclusive-slot-button-container">
        <div className="calendar-screen-reserve-tooltip-container">
          <div
            style={{
              backgroundImage: `url(${exclusiveIcon})`,
              cursor: isActiveMember ? "pointer" : "not-allowed",
            }}
            className="calendar-exclusive-slot-button "
            onClick={(event) => {
              if (!isActiveMember) return;
              event.stopPropagation();
              handleRetreatReserveClick(slot);
            }}
          ></div>
        </div>
      </div>
    );
  };

  const getExclusiveSlotReserveButton = (slot: any) => {
    const isAvailableForPurchase = isActiveMember
      ? slot?.purchase_access?.includes(userProfileData?.credit?.membership?.id)
      : false;

    let availableForMembershipName = null;

    if (slot?.purchase_access?.length > 0) {
      const availableForMembership = membershipListData?.data?.content?.find(
        (membership: any) => membership.id === slot?.purchase_access[0],
      );
      availableForMembershipName = MEMBERSHIP_TYPE.find(
        (type) => type.value === availableForMembership?.membership_type,
      )?.name;
    }

    return (
      <div className="calendar-exclusive-slot-button-container">
        <div className="calendar-screen-reserve-tooltip-container">
          <div
            style={{
              backgroundImage: `url(${isAvailableForPurchase ? exclusiveForYouIcon : exclusiveIcon
                })`,
              cursor: isAvailableForPurchase ? "pointer" : "not-allowed",
            }}
            className="calendar-exclusive-slot-button "
            onClick={(event) => {
              if (!isAvailableForPurchase) return;
              event.stopPropagation();
              handleReserveClick(slot);
            }}
          ></div>
          {availableForMembershipName && !isAvailableForPurchase && (
            <div className="calendar-screen-reserve-tooltip-text">
              <img src={reserveSlotInfo} alt="info-icon" />
              <div>
                <>
                  Available only for
                  <br />
                  {availableForMembershipName} membership.
                </>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getCalenderHeaderText = () => {
    if (selectedTab === CALENDAR_RESERVATION_TYPE.DF_WORKSPACE) {
      return "Think & Create";
    } else if (selectedTab === CALENDAR_RESERVATION_TYPE.INNOVATE_SPACE) {
      return "Execute & Build";
    } else if (selectedTab === CALENDAR_RESERVATION_TYPE.PASSES) {
      return "Execution Guided Pass";
    } else {
      return "Collaborate & Align";
    }
  };

  const getSepratorNextImage = () => {
    const images = [sepratorFirstImage, sepratorSecondImage];
    const image = images[imageIndexRef.current % images.length];
    imageIndexRef.current += 1;
    return image;
  };

  const getSepratorText = () => {
    const creativeStrings = [
      "A calm, low-stimulation environment",
      "Lets thoughts emerge & problems untangle without friction",
    ];

    const innovateStrings = [
      "Deep work and action. Calibrated for momentum & delivery",
      "Help you get into & stay in a state of high-output, distraction-free focus.",
    ];

    const communicateStrings = [
      "Without disturbing the deep work in other zones",
      "Connection that doesn't cost the room its focus.",
    ];

    const getNonRepeatingRandom = (
      arr: string[],
      lastRef: React.MutableRefObject<string>,
    ) => {
      if (arr.length === 1) return arr[0];

      let next = arr[Math.floor(Math.random() * arr.length)];

      while (next === lastRef.current) {
        next = arr[Math.floor(Math.random() * arr.length)];
      }

      lastRef.current = next;
      return next;
    };

    if (selectedTab === CALENDAR_RESERVATION_TYPE.DF_WORKSPACE) {
      return getNonRepeatingRandom(creativeStrings, creativeLastRef);
    } else if (selectedTab === CALENDAR_RESERVATION_TYPE.INNOVATE_SPACE) {
      return getNonRepeatingRandom(innovateStrings, innovateLastRef);
    } else {
      return getNonRepeatingRandom(communicateStrings, communicateLastRef);
    }
  };

  return (
    <div className="wms-mobile-container">
      {slotReservationMutation?.isError ? (
        <SnackbarAlert type={"error"}>
          {slotReservationMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <div className="calendar-screen-header-sticky">

        <CalendarScreenHeader
          title={selectedTab !== CALENDAR_RESERVATION_TYPE.PASSES ? "A space to" : undefined}
          subTitle={getCalenderHeaderText()}
          showMonthYearSelection={hideCalendarStrip}
          selectedDate={selectedDate}
          setSelectedDate={handleDateChange}
        />
        <div className="calendar-tab-container">
          {CALENDAR_RESERVATION_TABS.map((type, idx) => (
            <div
              key={type.value}
              className={`calendar-tab-container-item ${selectedTab === type.value ? "selected-tab" : ""
                }`}
              onClick={() => {
                setSelectedTab(type.value);
              }}
              ref={(el) => {
                tabRefs.current[idx] = el;
              }}
            >
              {type.name}
            </div>
          ))}
        </div>
        <div
          className={`calendar-strip-wrapper ${hideCalendarStrip ? "hide" : "show"}`}
        >
          <CalendarStrip
            selectedDate={selectedDate}
            setSelectedDate={handleDateChange}
          />

        </div>
      </div>

      {isLoading ? (
        <AppLoader title={getLoadingTitles()} />
      ) : slotsData?.data?.content?.length > 0 && isLaunchDatePassed() ? (
        <div className="calendar-screen-main-container">
          <div className="calendar-slot-list-container">
            {slotsData?.data?.content?.map((slot: any, index: number) => {
              const reservation = !reservationError
                ? reservationsData?.data?.content?.find(
                  (reservation: any) =>
                    reservation?.service_slot_id === slot?.id,
                )
                : null;

              const isReserved = !!reservation;
              const slotTime = getWorkspaceSlotTime(slot?.start_time);
              const showSeparator = (index + 1) % 3 === 0;

              return (
                <div key={index}>
                  <div
                    className="calendar-slot-list-card"
                    onClick={() => handleBookingCardClick(slot, reservation)}
                  >
                    <div className="calendar-slot-info-main-container">
                      <div className="calendar-card-image-container">
                        <div className="calendar-card-time">{slotTime}</div>
                        <div
                          className="calendar-card-image"
                          style={
                            selectedTab ===
                              CALENDAR_RESERVATION_TYPE.DF_WORKSPACE ||
                              selectedTab ===
                              CALENDAR_RESERVATION_TYPE.MEETING_ROOMS
                              ? {
                                backgroundImage: `url(${getDFWIcons(
                                  slot?.start_time,
                                )})`,
                                backgroundColor: "black",
                                backgroundSize: "80% 80%",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "center",
                              }
                              : { backgroundImage: `url(${slot?.slot_image})` }
                          }
                        >
                          {slot?.event_type &&
                            slot?.service_type ===
                            CALENDAR_RESERVATION_TYPE.EVENTS && (
                              <div
                                className={`calendar-card-image-footer ${slot?.event_type === "IN_PERSON"
                                  ? "in-person"
                                  : "in-video"
                                  }`}
                              >
                                {slot?.event_type === "IN_PERSON" ? (
                                  "In Person"
                                ) : (
                                  <img
                                    src={eventVideoIcon}
                                    alt="calendar-in-person-icon"
                                  />
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="calendar-card-info-container">
                        <div className="calendar-card-title">{slot?.title}</div>
                        <div className="calendar-card-text-container">
                          <div style={{ width: "100%" }}>
                            {slot?.instructor && (
                              <div>{`${slot?.instructor?.first_name} ${slot?.instructor?.last_name}`}</div>
                            )}
                            <div className="calendar-card-location">
                              <img src={locationIcon} alt="location-logo" />
                              {slot?.location}
                            </div>
                            <div>
                              {getWorkspaceSlotDuration(
                                slot?.start_time,
                                slot?.end_time,
                              )}
                            </div>
                          </div>

                          {slot?.service_type ===
                            CALENDAR_RESERVATION_TYPE.RETREAT && !isReserved ? (
                            getRetreatReserveButton(slot)
                          ) : slot?.is_exclusive && !isReserved ? (
                            getExclusiveSlotReserveButton(slot)
                          ) : (
                            <button
                              className={`${isReserved
                                ? "calendar-slot-reserved-btn"
                                : "calendar-slot-reserve-btn"
                                }${showAnimation && index === 0 ? " animate" : ""
                                }`}
                              disabled={isReserved}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleReserveClick(slot);
                              }}
                            >
                              {isReserved ? "Reserved ✓" : "Reserve"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {showSeparator ? (
                    <div
                      className="calendar-slot-card-separator-container"
                      style={{
                        backgroundImage: `url(${getSepratorNextImage()})`,
                      }}
                    >
                      <span>{getSepratorText()}</span>
                    </div>
                  ) : (
                    <div className="calendar-slot-card-divider"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="calendar-screen-placeholder-container">
          <img src={placeholderGif} alt="placeholder-icon" />
          <p>{getPlaceholderTitle()}</p>
        </div>
      )}

      <ReserveAcknowledgementDialog
        open={showAcknowledgementDialog}
        onClose={() => setShowAcknowledgementDialog(false)}
        slotData={selectedSlot}
        onClickLetsDo={handleReservationConfirmClick}
        isLoading={slotReservationMutation?.isPending}
      />
      <BecomeMemberDialog
        profileStatus={userProfileData?.credit?.status}
        open={showBecomeMemberDialog}
        onClose={() => setShowBecomeMemberDialog(false)}
      />
      <InformationPopup
        open={showInformationPopup}
        onClose={() => setShowInformationPopup(false)}
        onClick={() => {
          setShowInformationPopup(false);
          setSelectedTabBar(MENU_ITEM.MEMBERSHIP);
        }}
        title={STRING.MEMBERSHIP_EXPIRE_INFO_TITLE}
        subTitle={STRING.MEMBERSHIP_EXPIRE_INFO_SUB_TITLE}
        buttonText={STRING.MEMBERSHIP_EXPIRE_INFO_BUTTON_TITLE}
      />
    </div>
  );
};

export default CalendarScreen;
